/**
 * Unified Toast Notification Utility
 * 
 * This utility provides a consistent interface for showing toast notifications
 * throughout the application, replacing console.log, console.error, console.warn statements
 * with user-friendly toast messages.
 */

import { toast } from 'sonner';

/**
 * Toast notification types
 */
export const TOAST_TYPES = {
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
  INFO: 'info',
  LOADING: 'loading'
};

/**
 * Show a success toast notification
 * @param {string} message - The message to display
 * @param {object} options - Additional options for the toast
 */
export const showSuccessToast = (message, options = {}) => {
  toast.success(message, {
    duration: 4000,
    ...options
  });
};

/**
 * Show an error toast notification
 * @param {string} message - The message to display
 * @param {object} options - Additional options for the toast
 */
export const showErrorToast = (message, options = {}) => {
  toast.error(message, {
    duration: 6000,
    ...options
  });
};

/**
 * Show a warning toast notification
 * @param {string} message - The message to display
 * @param {object} options - Additional options for the toast
 */
export const showWarningToast = (message, options = {}) => {
  toast.warning(message, {
    duration: 5000,
    ...options
  });
};

/**
 * Show an info toast notification
 * @param {string} message - The message to display
 * @param {object} options - Additional options for the toast
 */
export const showInfoToast = (message, options = {}) => {
  toast.info(message, {
    duration: 4000,
    ...options
  });
};

/**
 * Show a loading toast notification
 * @param {string} message - The message to display
 * @param {object} options - Additional options for the toast
 */
export const showLoadingToast = (message, options = {}) => {
  return toast.loading(message, {
    duration: Infinity,
    ...options
  });
};

/**
 * Dismiss a specific toast
 * @param {string|number} toastId - The ID of the toast to dismiss
 */
export const dismissToast = (toastId) => {
  toast.dismiss(toastId);
};

/**
 * Dismiss all toasts
 */
export const dismissAllToasts = () => {
  toast.dismiss();
};

/**
 * Replace console.log with appropriate toast notification
 * @param {string} message - The message that was being logged
 * @param {any} data - Additional data that was being logged (optional)
 * @param {object} options - Toast options
 */
export const replaceConsoleLog = (message, data = null, options = {}) => {
  // In development, we might want to show more detailed info
  if (process.env.NODE_ENV === 'development' && data) {
    showInfoToast(`${message}${data ? `: ${JSON.stringify(data)}` : ''}`, options);
  } else {
    showInfoToast(message, options);
  }
};

/**
 * Replace console.error with error toast notification
 * @param {string} message - The error message
 * @param {Error|any} error - The error object (optional)
 * @param {object} options - Toast options
 */
export const replaceConsoleError = (message, error = null, options = {}) => {
  let errorMessage = message;
  
  // Extract meaningful error message
  if (error) {
    if (error.response?.data?.error) {
      errorMessage = error.response.data.error;
    } else if (error.message) {
      errorMessage = error.message;
    } else if (typeof error === 'string') {
      errorMessage = error;
    }
  }
  
  showErrorToast(errorMessage, {
    description: process.env.NODE_ENV === 'development' ? message : undefined,
    ...options
  });
};

/**
 * Replace console.warn with warning toast notification
 * @param {string} message - The warning message
 * @param {any} data - Additional data (optional)
 * @param {object} options - Toast options
 */
export const replaceConsoleWarn = (message, data = null, options = {}) => {
  let warningMessage = message;
  
  if (data && process.env.NODE_ENV === 'development') {
    warningMessage = `${message}${data ? `: ${JSON.stringify(data)}` : ''}`;
  }
  
  showWarningToast(warningMessage, options);
};

/**
 * Replace console.info with info toast notification
 * @param {string} message - The info message
 * @param {any} data - Additional data (optional)
 * @param {object} options - Toast options
 */
export const replaceConsoleInfo = (message, data = null, options = {}) => {
  let infoMessage = message;
  
  if (data && process.env.NODE_ENV === 'development') {
    infoMessage = `${message}${data ? `: ${JSON.stringify(data)}` : ''}`;
  }
  
  showInfoToast(infoMessage, options);
};

/**
 * Handle API errors with appropriate toast notifications
 * @param {Error} error - The API error
 * @param {string} defaultMessage - Default message if no specific error message
 * @param {object} options - Toast options
 */
export const handleApiError = (error, defaultMessage = 'An error occurred', options = {}) => {
  let errorMessage = defaultMessage;
  
  if (error?.response?.data?.error) {
    errorMessage = error.response.data.error;
  } else if (error?.response?.data?.message) {
    errorMessage = error.response.data.message;
  } else if (error?.message) {
    errorMessage = error.message;
  }
  
  showErrorToast(errorMessage, options);
};

/**
 * Handle network errors with appropriate toast notifications
 * @param {Error} error - The network error
 * @param {object} options - Toast options
 */
export const handleNetworkError = (error, options = {}) => {
  let errorMessage = 'Network error occurred';
  
  if (error?.message?.includes('Network Error')) {
    errorMessage = 'Please check your internet connection';
  } else if (error?.message?.includes('timeout')) {
    errorMessage = 'Request timed out. Please try again';
  } else if (error?.message) {
    errorMessage = error.message;
  }
  
  showErrorToast(errorMessage, {
    action: {
      label: 'Retry',
      onClick: () => window.location.reload()
    },
    ...options
  });
};

/**
 * Show a toast for successful operations
 * @param {string} operation - The operation that succeeded
 * @param {object} options - Toast options
 */
export const showOperationSuccess = (operation, options = {}) => {
  showSuccessToast(`${operation} completed successfully`, options);
};

/**
 * Show a toast for failed operations
 * @param {string} operation - The operation that failed
 * @param {Error} error - The error that occurred
 * @param {object} options - Toast options
 */
export const showOperationError = (operation, error, options = {}) => {
  handleApiError(error, `${operation} failed`, options);
};

/**
 * Show a toast for loading states
 * @param {string} operation - The operation being performed
 * @param {object} options - Toast options
 * @returns {string|number} - Toast ID for dismissing later
 */
export const showOperationLoading = (operation, options = {}) => {
  return showLoadingToast(`${operation} in progress...`, options);
};

/**
 * Complete a loading toast with success
 * @param {string|number} toastId - The loading toast ID
 * @param {string} message - Success message
 */
export const completeLoadingWithSuccess = (toastId, message) => {
  toast.success(message, { id: toastId });
};

/**
 * Complete a loading toast with error
 * @param {string|number} toastId - The loading toast ID
 * @param {string} message - Error message
 */
export const completeLoadingWithError = (toastId, message) => {
  toast.error(message, { id: toastId });
};

// Export all functions as default for convenience
export default {
  showSuccessToast,
  showErrorToast,
  showWarningToast,
  showInfoToast,
  showLoadingToast,
  dismissToast,
  dismissAllToasts,
  replaceConsoleLog,
  replaceConsoleError,
  replaceConsoleWarn,
  replaceConsoleInfo,
  handleApiError,
  handleNetworkError,
  showOperationSuccess,
  showOperationError,
  showOperationLoading,
  completeLoadingWithSuccess,
  completeLoadingWithError,
  TOAST_TYPES
};
